import React from "react";
import { useFormat, useTranslation } from "../../i18n";

/**
 * The streak flame.
 *
 * `lit` is the week's on/off switch: nothing earned yet means an actually cold
 * fire — grey logs, no flame, no embers, no glow. Not a dim flame, because a
 * dim flame still reads as "burning" and the whole point is that the widget
 * should look *different* the moment you do something. The reward for the first
 * action is the fire existing at all.
 *
 * `intensity` runs 0 → 1 (this week's credits over the threshold) and drives
 * everything above that switch: flame height, brightness, ember count. So the
 * picture *is* the progress bar — a small flame means you're one comment in, a
 * tall one means the week is secured.
 *
 * `igniting` plays the catch: the flame springs up from nothing. It fires once,
 * on the transition into lit, and is what makes the first action of the week
 * feel like it landed rather than like a number changed.
 *
 * Every flame element pivots at BASE_Y — the point where the flame meets the
 * ground. Rotating or scaling around a shape's own centre makes a flame look
 * like a wobbling leaf; anchoring the base is what makes the tip sway while the
 * root stays put, which is how fire actually moves.
 *
 * All motion is CSS keyframes on SVG nodes — no rAF loop, no canvas — so it
 * costs nothing off-screen and stops dead under prefers-reduced-motion.
 */

// Single source of truth for the pivot, shared with the CSS via a custom prop.
const BASE_Y = 92;

export default function Campfire({
  intensity = 0,
  lit = false,
  size = 132,
  stoked = false,
  igniting = false,
  // Increments on every stoke. Used as a React key so the burst remounts and
  // replays — a CSS animation on a persistent node only ever runs once.
  stokeId = 0,
}) {
  const { t } = useTranslation("widgets");
  const fmt = useFormat();
  const level = Math.max(0, Math.min(1, intensity));
  // A just-lit fire still needs a visible flame, hence the floor.
  const flame = lit ? 0.35 + level * 0.65 : 0;
  const emberCount = Math.round(3 + level * 5);

  const classes = ["campfire"];
  if (stoked) classes.push("campfire--stoked");
  if (igniting) classes.push("campfire--igniting");

  return (
    <svg
      viewBox="0 0 120 120"
      width={size}
      height={size}
      role="img"
      aria-label={
        lit
          ? t("campfire.burning", { percent: fmt.number(Math.round(level * 100)) })
          : t("campfire.unlit")
      }
      className={classes.join(" ")}
      style={{ "--flame": flame, "--cf-base": `${BASE_Y}px` }}
    >
      <defs>
        <radialGradient id="cf-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#fb923c" stopOpacity="0.5" />
          <stop offset="55%" stopColor="#f97316" stopOpacity="0.14" />
          <stop offset="100%" stopColor="#f97316" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="cf-outer" x1="50%" y1="0%" x2="50%" y2="100%">
          <stop offset="0%" stopColor="#fdba74" />
          <stop offset="45%" stopColor="#f97316" />
          <stop offset="100%" stopColor="#ea580c" />
        </linearGradient>
        <linearGradient id="cf-inner" x1="50%" y1="0%" x2="50%" y2="100%">
          <stop offset="0%" stopColor="#fef3c7" />
          <stop offset="55%" stopColor="#fbbf24" />
          <stop offset="100%" stopColor="#f59e0b" />
        </linearGradient>
        <linearGradient id="cf-core" x1="50%" y1="0%" x2="50%" y2="100%">
          <stop offset="0%" stopColor="#fffbeb" />
          <stop offset="100%" stopColor="#fde68a" />
        </linearGradient>
      </defs>

      {/* Ground glow, centred on the flame base. Only when something burns. */}
      {lit && (
        <ellipse
          cx="60"
          cy={BASE_Y - 2}
          rx="34"
          ry="12"
          fill="url(#cf-glow)"
          className="cf-glow"
        />
      )}

      {/* Cold hearth. Nothing but a smudge of ash and a few dead embers, so the
          unlit state reads as "the fire is out" rather than as a widget that
          failed to render — and so there is a place for the flame to appear. */}
      {!lit && (
        <g className="cf-ash">
          <ellipse cx="60" cy={BASE_Y} rx="20" ry="4.5" fill="#d4d4d8" opacity="0.5" />
          {[-9, 1, 10].map((dx, index) => (
            <circle
              key={dx}
              cx={60 + dx}
              cy={BASE_Y - 1 - (index % 2)}
              r={index === 1 ? 2 : 1.6}
              fill="#a1a1aa"
              opacity="0.7"
            />
          ))}
        </g>
      )}

      {lit && (
        <g className="cf-flames">
          {/* Outer flame — teardrop rising from BASE_Y to y≈24 */}
          <path
            className="cf-flame cf-flame--outer"
            d="M60 24c14 18 24 30 24 42a24 24 0 0 1-48 0c0-12 10-24 24-42z"
            fill="url(#cf-outer)"
          />
          {/* Inner flame — brighter, faster, offset so the two never sync up */}
          <path
            className="cf-flame cf-flame--inner"
            d="M60 48c8 11 13 18 13 25a13 13 0 0 1-26 0c0-7 5-14 13-25z"
            fill="url(#cf-inner)"
          />
          {/* Hot core at the base */}
          <ellipse
            className="cf-flame cf-flame--core"
            cx="60"
            cy={BASE_Y - 10}
            rx="6"
            ry="8"
            fill="url(#cf-core)"
          />
        </g>
      )}

      {/* Burst of sparks thrown out when the fire is stoked. Rendered only for
          the flare, keyed so each stoke replays from the start. */}
      {stoked && (
        <g key={stokeId} className="cf-burst">
          {Array.from({ length: 14 }).map((_, index) => {
            const angle = (index / 14) * Math.PI * 2 + (stokeId % 7) * 0.22;
            const spread = 26 + ((index * 13) % 22);
            return (
              <circle
                key={index}
                className="cf-spark"
                cx="60"
                cy={BASE_Y - 18}
                r={index % 4 === 0 ? 2.2 : 1.5}
                fill={index % 3 === 0 ? "#fde68a" : "#fb923c"}
                style={{
                  "--sx": `${Math.cos(angle) * spread}px`,
                  // Biased upward: sparks rise on the draught rather than
                  // scattering evenly like a firework.
                  "--sy": `${Math.sin(angle) * spread - 30}px`,
                  animationDelay: `${(index % 5) * 0.035}s`,
                }}
              />
            );
          })}
        </g>
      )}

      {/* Embers drifting up. Deterministic offsets keep them from clumping.
          Nothing drifts off a cold fire, so these belong to the lit state. */}
      {lit &&
        Array.from({ length: emberCount }).map((_, index) => {
          const x = 42 + ((index * 11) % 37);
          const delay = (index * 0.53) % 3;
          const scale = 0.6 + ((index * 7) % 5) / 10;
          return (
            <circle
              key={index}
              className="cf-ember"
              cx={x}
              cy={BASE_Y - 6}
              r={1.5 * scale}
              fill={index % 3 === 0 ? "#fde68a" : "#fb923c"}
              style={{ animationDelay: `${delay}s` }}
            />
          );
        })}
    </svg>
  );
}
